// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  VirtualHub,
  VirtualHubsListByResourceGroupOptionalParams,
  VirtualHubsListOptionalParams,
  VirtualHubsGetOptionalParams,
  VirtualHubsGetResponse,
  VirtualHubsCreateOrUpdateOptionalParams,
  VirtualHubsCreateOrUpdateResponse,
  TagsObject,
  VirtualHubsUpdateTagsOptionalParams,
  VirtualHubsUpdateTagsResponse,
  VirtualHubsDeleteOptionalParams,
  VirtualHubsGetEffectiveVirtualHubRoutesOptionalParams,
  VirtualHubsGetEffectiveVirtualHubRoutesResponse,
  GetInboundRoutesParameters,
  VirtualHubsGetInboundRoutesOptionalParams,
  VirtualHubsGetInboundRoutesResponse,
  GetOutboundRoutesParameters,
  VirtualHubsGetOutboundRoutesOptionalParams,
  VirtualHubsGetOutboundRoutesResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a VirtualHubs. */
export interface VirtualHubs {
  /**
   * Lists all the VirtualHubs in a resource group.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param options The options parameters.
   */
  listByResourceGroup(
    resourceGroupName: string,
    options?: VirtualHubsListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<VirtualHub>;
  /**
   * Lists all the VirtualHubs in a subscription.
   * @param options The options parameters.
   */
  list(options?: VirtualHubsListOptionalParams): PagedAsyncIterableIterator<VirtualHub>;
  /**
   * Retrieves the details of a VirtualHub.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubsGetOptionalParams,
  ): Promise<VirtualHubsGetResponse>;
  /**
   * Creates a VirtualHub resource if it doesn't exist else updates the existing VirtualHub.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param virtualHubParameters Parameters supplied to create or update VirtualHub.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    virtualHubName: string,
    virtualHubParameters: VirtualHub,
    options?: VirtualHubsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualHubsCreateOrUpdateResponse>,
      VirtualHubsCreateOrUpdateResponse
    >
  >;
  /**
   * Creates a VirtualHub resource if it doesn't exist else updates the existing VirtualHub.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param virtualHubParameters Parameters supplied to create or update VirtualHub.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    virtualHubName: string,
    virtualHubParameters: VirtualHub,
    options?: VirtualHubsCreateOrUpdateOptionalParams,
  ): Promise<VirtualHubsCreateOrUpdateResponse>;
  /**
   * Updates VirtualHub tags.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param virtualHubParameters Parameters supplied to update VirtualHub tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    virtualHubName: string,
    virtualHubParameters: TagsObject,
    options?: VirtualHubsUpdateTagsOptionalParams,
  ): Promise<VirtualHubsUpdateTagsResponse>;
  /**
   * Deletes a VirtualHub.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes a VirtualHub.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubsDeleteOptionalParams,
  ): Promise<void>;
  /**
   * Gets the effective routes configured for the Virtual Hub resource or the specified resource .
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param options The options parameters.
   */
  beginGetEffectiveVirtualHubRoutes(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubsGetEffectiveVirtualHubRoutesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualHubsGetEffectiveVirtualHubRoutesResponse>,
      VirtualHubsGetEffectiveVirtualHubRoutesResponse
    >
  >;
  /**
   * Gets the effective routes configured for the Virtual Hub resource or the specified resource .
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param options The options parameters.
   */
  beginGetEffectiveVirtualHubRoutesAndWait(
    resourceGroupName: string,
    virtualHubName: string,
    options?: VirtualHubsGetEffectiveVirtualHubRoutesOptionalParams,
  ): Promise<VirtualHubsGetEffectiveVirtualHubRoutesResponse>;
  /**
   * Gets the inbound routes configured for the Virtual Hub on a particular connection.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param getInboundRoutesParameters Parameters supplied to get the inbound routes for a connection
   *                                   resource.
   * @param options The options parameters.
   */
  beginGetInboundRoutes(
    resourceGroupName: string,
    virtualHubName: string,
    getInboundRoutesParameters: GetInboundRoutesParameters,
    options?: VirtualHubsGetInboundRoutesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualHubsGetInboundRoutesResponse>,
      VirtualHubsGetInboundRoutesResponse
    >
  >;
  /**
   * Gets the inbound routes configured for the Virtual Hub on a particular connection.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param getInboundRoutesParameters Parameters supplied to get the inbound routes for a connection
   *                                   resource.
   * @param options The options parameters.
   */
  beginGetInboundRoutesAndWait(
    resourceGroupName: string,
    virtualHubName: string,
    getInboundRoutesParameters: GetInboundRoutesParameters,
    options?: VirtualHubsGetInboundRoutesOptionalParams,
  ): Promise<VirtualHubsGetInboundRoutesResponse>;
  /**
   * Gets the outbound routes configured for the Virtual Hub on a particular connection.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param getOutboundRoutesParameters Parameters supplied to get the outbound routes for a connection
   *                                    resource.
   * @param options The options parameters.
   */
  beginGetOutboundRoutes(
    resourceGroupName: string,
    virtualHubName: string,
    getOutboundRoutesParameters: GetOutboundRoutesParameters,
    options?: VirtualHubsGetOutboundRoutesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualHubsGetOutboundRoutesResponse>,
      VirtualHubsGetOutboundRoutesResponse
    >
  >;
  /**
   * Gets the outbound routes configured for the Virtual Hub on a particular connection.
   * @param resourceGroupName The resource group name of the VirtualHub.
   * @param virtualHubName The name of the VirtualHub.
   * @param getOutboundRoutesParameters Parameters supplied to get the outbound routes for a connection
   *                                    resource.
   * @param options The options parameters.
   */
  beginGetOutboundRoutesAndWait(
    resourceGroupName: string,
    virtualHubName: string,
    getOutboundRoutesParameters: GetOutboundRoutesParameters,
    options?: VirtualHubsGetOutboundRoutesOptionalParams,
  ): Promise<VirtualHubsGetOutboundRoutesResponse>;
}
